import { ArrowRight } from "lucide-react";
import { useEffect, useRef, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import { AffiliateLink } from "./AffiliateLink";
import { getAffiliateLink } from "@/lib/affiliate-registry";
import { type AffiliateId } from "@/config/affiliateLinks";
import { getAffiliateTheme } from "@/lib/affiliate-theme";
import { checkNoOverlap } from "@/lib/dev-overlap-check";
import { useAdImpression } from "@/hooks/useAdImpression";
import type { AdPlacement } from "@/lib/ad-tracking";

interface AffiliateCtaBoxProps {
  affiliateId: AffiliateId | string;
  title: string;
  text?: string;
  cta?: string;
  microcopy?: string;
  className?: string;
  compact?: boolean;
  placement?: AdPlacement | string;
  /** Deprecated: ikon udledes nu automatisk fra kategori. Beholdes for bagudkompatibilitet. */
  icon?: React.ReactNode;
}

export function AffiliateCtaBox({
  affiliateId,
  title,
  text,
  cta,
  microcopy,
  className,
  compact = false,
  placement = "sidebar",
}: AffiliateCtaBoxProps) {
  const link = getAffiliateLink(affiliateId);
  const impressionRef = useAdImpression<HTMLElement>(link?.id, placement);
  const rootRef = (el: HTMLElement | null) => {
    rootRefInner.current = el;
    impressionRef.current = el;
  };
  const rootRefInner = useRef<HTMLElement | null>(null);
  const adPillRef = useRef<HTMLSpanElement | null>(null);
  const categoryBadgeRef = useRef<HTMLSpanElement | null>(null);
  const ctaRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!link) return;

    const run = () => {
      checkNoOverlap(`AffiliateCtaBox[${link.id}]`, [
        { label: "Kategori-badge", el: categoryBadgeRef.current },
        { label: "Annonce-mærke", el: adPillRef.current },
        { label: "CTA", el: ctaRef.current },
      ]);
    };

    const raf = requestAnimationFrame(run);
    window.addEventListener("resize", run);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", run);
    };
  }, [link, title, text, cta, microcopy, compact]);

  if (!link) return null;

  const theme = getAffiliateTheme(link.category);
  const Icon = theme.icon;

  const styleVars = {
    "--ad-accent": theme.accentHsl,
    "--ad-soft": theme.softHsl,
  } as CSSProperties;

  return (
    <aside
      ref={rootRef}
      style={styleVars}
      className={cn(
        "group relative overflow-hidden rounded-2xl border border-[hsl(var(--ad-accent)/0.25)] bg-card text-card-foreground shadow-sm hover:shadow-lg hover:border-[hsl(var(--ad-accent)/0.5)] transition-all flex flex-col",
        compact ? "p-4 pl-5 gap-2.5" : "p-5 pl-6 gap-3",
        className,
      )}
      data-affiliate-cta={link.id}
    >
      {/* Accent-stribe i venstre kant */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1.5 bg-[hsl(var(--ad-accent))] transition-all group-hover:w-2"
      />
      {/* Subtil kategori-tonet baggrund */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[hsl(var(--ad-soft)/0.6)] via-transparent to-transparent"
      />

      <div className="relative z-10 flex items-start justify-between gap-2">
        <span
          ref={categoryBadgeRef}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ad-accent))] bg-[hsl(var(--ad-soft))] px-2.5 py-1 rounded-full shrink-0"
        >
          <Icon className="w-3.5 h-3.5" aria-hidden="true" />
          {theme.label}
        </span>
        <span
          ref={adPillRef}
          className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground shrink-0 mt-0.5"
        >
          Annonce
        </span>
      </div>

      <div className="relative z-10 flex items-center gap-3">
        <div
          className={cn(
            "inline-flex items-center justify-center rounded-xl bg-[hsl(var(--ad-accent))] text-white shadow-sm shrink-0",
            compact ? "w-11 h-11" : "w-14 h-14",
          )}
        >
          <Icon className={compact ? "w-5 h-5" : "w-7 h-7"} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground leading-tight">
            Fra annoncør
          </p>
          <p className="text-sm font-bold text-foreground truncate">{link.advertiser}</p>
        </div>
      </div>

      <h3
        className={cn(
          "relative z-10 font-display font-semibold leading-snug text-foreground",
          compact ? "text-base" : "text-lg",
        )}
      >
        {title}
      </h3>
      {text && (
        <p
          className={cn(
            "relative z-10 text-muted-foreground leading-relaxed",
            compact ? "text-xs" : "text-sm",
          )}
        >
          {text}
        </p>
      )}
      <div ref={ctaRef} className={cn("relative z-10 mt-auto space-y-1.5", compact ? "" : "pt-1")}>
        <AffiliateLink
          affiliateId={affiliateId}
          variant="button"
          placement={placement}
          className="w-full justify-center group/btn border-transparent text-white hover:brightness-110"
          style={{ backgroundColor: `hsl(${theme.accentHsl})` } as CSSProperties}
        >
          <span>{cta ?? link.defaultCta}</span>
          <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" aria-hidden="true" />
        </AffiliateLink>
        {microcopy && (
          <p className="text-[11px] text-center text-muted-foreground">{microcopy}</p>
        )}
      </div>

      {!compact && (
        <p className="relative z-10 text-[11px] text-muted-foreground leading-snug">
          Affiliate-link. Min Nye Elbil kan modtage kommission – det påvirker ikke prisen for dig.
        </p>
      )}
    </aside>
  );
}
